import take from 'lodash/take';
import toUpper from 'lodash/toUpper';
import compact from 'lodash/compact';
import flow from 'lodash/flow';
import first from 'lodash/first';
import { getMessageBody } from '@app/dashboard/messaging/lib/utils';
import { CHANNEL_TYPE } from './messaging.interface';
import { Role } from '@app/lib/roles.const';
export const DEFAULT_BROADCAST_NAME_V1 = 'Group Channel';
export function nameInitials(obj) {
    return compact([obj.first_name, obj.last_name])
        .map(flow([first, toUpper]))
        .join('');
}
export class Channel {
    constructor(sdkInstance, loggedInUserId) {
        this.loggedInUserId = loggedInUserId;
        this.internalSdkInstance = sdkInstance;
        if (this.hasTeam) {
            // Get Team ID from customType, format is team_{id}
            this.teamId = Number(sdkInstance.customType.split('_')[1]);
        }
    }
    get url() {
        return this.sdkInstance ? this.sdkInstance.url : undefined;
    }
    get name() {
        switch (this.type) {
            case CHANNEL_TYPE.DIRECT:
                return this.getOtherParticipant(this.participants).nickname;
            case CHANNEL_TYPE.BROADCAST:
                if (this.sdkInstance.name === DEFAULT_BROADCAST_NAME_V1)
                    return this.getChannelName();
                return this.sdkInstance.name ? this.sdkInstance.name : this.getChannelName();
            case CHANNEL_TYPE.TEAM:
                const driver = this.participants.filter((member) => member.metaData['role'] === Role.DRIVER)[0];
                return driver === null || driver === void 0 ? void 0 : driver.nickname;
            default:
                return this.getChannelName();
        }
    }
    set name(name) {
        this.sdkInstance.name = name;
    }
    get type() {
        var _a, _b;
        const channelType = this.sdkInstance.customType;
        if (channelType.startsWith(CHANNEL_TYPE.TEAM))
            return CHANNEL_TYPE.TEAM;
        else if (((_b = (_a = this.sdkInstance) === null || _a === void 0 ? void 0 : _a.members) === null || _b === void 0 ? void 0 : _b.length) === 2 && this.isGroupChannel)
            return CHANNEL_TYPE.DIRECT;
        else if (channelType === CHANNEL_TYPE.BROADCAST)
            return channelType;
        return CHANNEL_TYPE.GROUP;
    }
    get isGroupChannel() {
        /* isGroupChannel will be used as a function if used with group channel instance (this will be the case if messaging
        * is used on main thread)
        * For shared worker implementation group channel instance will have isGroupChannel as boolean.
        */
        if (this.sdkInstance && typeof this.sdkInstance.isGroupChannel === 'function') {
            return this.sdkInstance.isGroupChannel();
        }
        else if (this.sdkInstance.hasOwnProperty('isGroupChannelExt')) {
            return this.sdkInstance.isGroupChannelExt;
        }
        else {
            return true;
        }
    }
    get unreadMessageCount() {
        return this.sdkInstance.unreadMessageCount;
    }
    get lastMessage() {
        return this.sdkInstance.lastMessage;
    }
    get lastMessageBody() {
        return getMessageBody(this.lastMessage);
    }
    get timeOfLastMessageInMilliseconds() {
        var _a;
        return (_a = this.lastMessage) === null || _a === void 0 ? void 0 : _a.createdAt;
    }
    get createdAt() {
        return this.sdkInstance.createdAt;
    }
    get participantCount() {
        if (this.sdkInstance.memberCount)
            return this.sdkInstance.memberCount;
        else
            return this.participants.length;
    }
    get participants() {
        return this.sdkInstance.members;
    }
    get teamName() {
        return this.hasTeam ? this.sdkInstance.name : '';
    }
    get hasTeam() {
        return this.type === CHANNEL_TYPE.TEAM;
    }
    get isArchived() {
        return this.sdkInstance.isHidden;
    }
    get isNewChannel() {
        return !this.lastMessage;
    }
    get sdkInstance() {
        return this.internalSdkInstance;
    }
    generateChannelInitials() {
        const otherParticipant = this.participants.find((participant) => participant.userId !== this.loggedInUserId);
        return nameInitials(otherParticipant.metaData);
    }
    getOtherParticipant(participants) {
        return participants.find((participant) => participant.userId !== this.loggedInUserId);
    }
    getOtherParticipants(participants) {
        return participants.filter((participant) => participant.userId !== this.loggedInUserId);
    }
    getChannelName() {
        var _a;
        if ((_a = this.participants) === null || _a === void 0 ? void 0 : _a.length) {
            return take(this.getOtherParticipants(this.participants), 3)
                .map((participant) => participant.metaData.first_name)
                .join(', ');
        }
        else {
            // only called for broadcast and default cases
            return $localize `Broadcast with ${this.participantCount} members`;
        }
    }
}
